import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { X, ChevronLeft, ChevronRight, Check } from 'lucide-react';

const OnboardingTour = ({ user, onComplete, onSkip, onNavigate }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const getTourSteps = () => {
    let _orgName = 'CounselCore';
    try {
      const _org = JSON.parse(sessionStorage.getItem('organization') || '{}');
      if (_org?.name) _orgName = _org.name;
    } catch (_e) { /* noop */ }
    const baseSteps = [
      {
        target: null,
        title: `Welcome to ${_orgName}! 👋`,
        content: 'Let\'s take a quick tour to help you get started. This will only take 2 minutes.',
        position: 'center',
        tab: null
      },
      {
        target: '[data-tour="dashboard"]',
        title: 'Your Dashboard',
        content: 'This is your personalized dashboard. You can customize widgets, view quick stats, and access external systems here.',
        position: 'bottom',
        tab: 'dashboard'
      },
      {
        target: '[data-tour="clinical-tab"]',
        title: 'Clinical Management',
        content: 'Access all your clients, sessions, progress notes, and reports from this tab. This is where you\'ll spend most of your time.',
        position: 'bottom',
        tab: 'client-care'
      },
      {
        target: '[data-tour="add-client"]',
        title: 'Add New Clients',
        content: 'Click here to add new clients. The system automatically checks for duplicates to keep your data clean.',
        position: 'bottom',
        tab: 'client-care'
      },
      {
        target: '[data-tour="calendar"]',
        title: 'Interactive Calendar',
        content: 'Manage all your sessions with the interactive calendar. Create, edit, and track sessions with ease.',
        position: 'bottom',
        tab: 'calendar'
      },
      {
        target: '[data-tour="analytics"]',
        title: 'Analytics & Insights',
        content: 'View comprehensive analytics, trends, and performance insights. Track your progress and identify areas for improvement.',
        position: 'bottom',
        tab: 'analytics'
      },
      {
        target: '[data-tour="notifications"]',
        title: 'Notification Center',
        content: 'Stay updated with all notifications about sessions, notes, and important events. Customize your notification preferences here.',
        position: 'left',
        tab: 'dashboard'
      }
    ];

    // Add admin-specific steps
    if (user?.role === 'admin') {
      baseSteps.push({
        target: '[data-tour="admin-tab"]',
        title: 'Administration',
        content: 'As an admin, you have access to advanced features like user management, billing, smart data tools, and system configuration.',
        position: 'bottom',
        tab: 'administration'
      });
    }

    baseSteps.push({
      target: null,
      title: 'You\'re All Set! 🎉',
      content: 'You now know the basics! Explore the app at your own pace. You can replay this tour anytime from your profile menu.',
      position: 'center',
      tab: 'dashboard'
    });

    return baseSteps;
  };

  const steps = getTourSteps();

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem('onboarding_completed', 'true');
    localStorage.setItem('onboarding_completed_at', new Date().toISOString());
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  const handleSkip = () => {
    if (confirm('Are you sure you want to skip the tour? You can replay it anytime from your profile menu.')) {
      handleComplete();
      if (onSkip) onSkip();
    }
  };

  useEffect(() => {
    const step = steps[currentStep];
    
    // Navigate to the required tab
    if (step.tab && onNavigate) {
      onNavigate(step.tab);
    }
    
    // Wait a moment for navigation, then scroll to element
    setTimeout(() => {
      if (step.target) {
        const element = document.querySelector(step.target);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }, 300);
  }, [currentStep]);

  // Don't render anything if tour is not visible
  if (!isVisible) {
    return null;
  }

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-[9998]" />

      {/* Spotlight effect for targeted elements */}
      {currentStepData.target && (
        <style dangerouslySetInnerHTML={{
          __html: `
            ${currentStepData.target} {
              position: relative;
              z-index: 9999 !important;
              box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5), 0 0 20px rgba(59, 130, 246, 0.5) !important;
              border-radius: 8px;
            }
          `
        }} />
      )}

      {/* Tour Card */}
      <div 
        className={`fixed z-[10000] ${
          currentStepData.position === 'center' 
            ? 'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2' 
            : 'bottom-20 right-8'
        }`}
        style={{ maxWidth: '450px', width: '90vw' }}
      >
        <Card className="shadow-2xl border-2 border-blue-500">
          <CardContent className="p-6">
            {/* Header */}
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{currentStepData.title}</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Step {currentStep + 1} of {steps.length}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSkip}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Content */}
            <p className="text-gray-700 mb-6 leading-relaxed">
              {currentStepData.content}
            </p>

            {/* Progress Bar */}
            <div className="mb-6">
              <div className="flex space-x-1">
                {steps.map((_, index) => (
                  <div
                    key={index}
                    className={`h-2 flex-1 rounded ${
                      index <= currentStep ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={isFirstStep}
                className="flex items-center"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>

              {isLastStep ? (
                <Button
                  onClick={handleComplete}
                  className="bg-green-600 hover:bg-green-700 flex items-center"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Get Started!
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  className="bg-blue-600 hover:bg-blue-700 flex items-center"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>

            {/* Skip link */}
            {!isLastStep && (
              <div className="text-center mt-4">
                <button
                  onClick={handleSkip}
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  Skip tour
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default OnboardingTour;
